import { Trans } from '@lingui/react'
import classNames from 'clsx'
import { bool, number, shape, string } from 'prop-types'
import { compose } from 'redux'

import { PAGE_TYPE_CART, TENANT_AT, TENANT_IT } from 'shared/consts'
import { AD_SERVER_METADATA_DATA_ATTR } from 'shared/consts/adServer'
import {
  ANALYTICS_ATTR_IMAGE,
  ANALYTICS_ATTR_TITLE,
} from 'shared/consts/crosssellWidgets'
import { PRESCRIPTION_TYPE_NO_PRESCRIPTION } from 'shared/consts/productTemplates'
import { TOP_DEALS_LABEL } from 'shared/experiments/consts'
import { getExperiment } from 'shared/experiments/utils/experiments'
import { isMarketplace } from 'shared/experiments/utils/featureFlags'
import getProductUrl from 'shared/utils/getProductUrl'
import { isEmpty } from 'shared/utils/objectUtils'
import Button from 'views/components/atoms/Button'
import ButtonAsLink from 'views/components/molecules/ButtonAsLink'
import ButtonImage from 'views/components/molecules/ButtonImage'
import ImageWithDiscountBadge from 'views/components/molecules/ImageWithDiscountBadge'
import StarRating from 'views/components/molecules/StarRating'
import ProductPrice from 'views/components/organisms/ProductPrice'
import { SELLER_MODAL_ID } from 'views/components/organisms/SellerModal/names'
import ProductSliderItemShippingPrice from 'views/components/organisms/SliderHorizontal/ProductSliderItem/ProductSliderItemShippingPrice'
import { getSellerModalHook } from 'views/containers/browser/SellerModalContainer/clientsideHook'
import withMountPoints from 'views/enhancers/withMountPoints'
import withShopAndRuntimeConfig from 'views/enhancers/withShopAndRuntimeConfig'
import { getDiscountToShow } from 'views/utils/discount'
import { isAdServerEnabled } from 'views/utils/featureToggleUtils'
import {
  mountPointsType,
  ProductType,
  publicRuntimeConfigType,
} from 'views/utils/propTypes'
import { adServerMetadataType } from 'views/utils/propTypes/adserver'
import { sizesPropType } from 'views/utils/propTypes/img'

import { getAmount, resolveLazyLoad } from './helpers'

import './ProductSliderItem.scss'

const ProductSliderItem = props => {
  const {
    publicRuntimeConfig: {
      publicConfig: { deviceClass, tenant },
      pageProperties: { experiments, pageType, shopOrigin, featureToggles },
    },
    mountPoints,
    index,
    lazyLoad,
    noBorder,
    product,
    contextPid,
    redirectToCart,
    metadata,
    showBuyButton,
    goToProductButton,
    srcHeight,
    srcWidth,
    imageMinWidth,
    imageMaxWidth,
    imageSizes,
    thumbnail,
    whiteImageBg,
    wid,
  } = props

  const {
    canonicalURL,
    darreichung: pharmaForm,
    id,
    imageUrl,
    name,
    numberOfRatings,
    offerId,
    discount,
    gkAirAreaId,
    gkAirContextPid,
    gkAirTrackingToken,
    offersCount,
    prices,
    rating,
    rxByPrincipleReasonSet,
    sellerId,
    sellerName,
    shippingPrice,
    thumbnailURL,
    unitPricing,
  } = product

  const topDealExperiment = getExperiment(TOP_DEALS_LABEL, experiments)
  const shouldShowBuyButton = () => {
    if (typeof showBuyButton === 'boolean') {
      return showBuyButton
    } else if (typeof showBuyButton === 'string') {
      if (showBuyButton === 'false') {
        return false
      } else if (showBuyButton === 'true') {
        return true
      }
    }
  }

  const amount = getAmount(product)
  const discountsToShow = getDiscountToShow({ tenant, discount })

  const productMetadataContent = metadata[id] || {}
  const trackingUrls = productMetadataContent.trackingUrls || {}
  const isSponsored = Boolean(trackingUrls.clickUrl)
  const productUrl = trackingUrls.clickUrl || getProductUrl(canonicalURL)
  const addToCartPath = mountPoints.getAddToCart({
    cartAction: 'add',
    eventName: 'addToCart_crossSell',
    eventType: 'conversion',
    offer: offerId,
    prescriptionType: PRESCRIPTION_TYPE_NO_PRESCRIPTION,
    product: id,
    quantity: 1,
    redirectToCart,
  })
  const isMarketplaceEnabled = isMarketplace(experiments, tenant)

  const addToCartUrlWithTracking = trackingUrls.clickUrl
    ? `${trackingUrls.clickUrl}&url=${shopOrigin}${addToCartPath}`
    : undefined

  const sellerIdSAE = 'shop-apotheke'

  const showSellerBox =
    sellerId &&
    shouldShowBuyButton() &&
    isMarketplaceEnabled &&
    sellerId !== sellerIdSAE &&
    offersCount &&
    offersCount === 1

  const shouldDisplayAbPrice =
    !shouldShowBuyButton() && offersCount > 1 && isMarketplaceEnabled

  const className = classNames('o-ProductSliderItem u-text-align--center', {
    'o-ProductSliderItem--white-img-bg': whiteImageBg,
    'u-padding--top': topDealExperiment.isEnabled && tenant !== TENANT_IT,
  })

  const shouldIncludeProductMetadataContentAsDataAttribute =
    isAdServerEnabled(featureToggles) && !isEmpty(productMetadataContent)

  const sliderItemProps = {
    className,
    'data-product-id': id,
    'data-gk-air-area-id': gkAirAreaId,
    'data-gk-air-context-pid': gkAirContextPid,
    'data-gk-air-tracking-token': gkAirTrackingToken,
    [AD_SERVER_METADATA_DATA_ATTR]:
      shouldIncludeProductMetadataContentAsDataAttribute
        ? JSON.stringify(productMetadataContent)
        : undefined,
  }

  if (contextPid) {
    sliderItemProps['data-crosssell-context-pid'] = contextPid
  }

  return (
    <article {...sliderItemProps}>
      <div
        className={classNames('o-ProductSliderItem__main', {
          'o-ProductSliderItem__main--no-border': noBorder,
          'o-ProductSliderItem__main--with-seller-box':
            shouldShowBuyButton() || pageType === PAGE_TYPE_CART,
          'u-padding--bottom': !shouldShowBuyButton(),
        })}
      >
        {!trackingUrls.impressionUrl ? null : (
          <small className="o-ProductSliderItem__sponsoredBadge">
            <Trans id="general.label.sponsored" />
          </small>
        )}
        <header className="o-ProductSliderItem__header u-position--relative u-margin-x-small--bottom">
          <div
            className="o-ProductSliderItem__image u-margin-large--bottom o-ProductSliderItem__header__item"
            data-clientside-hook="slider__image"
          >
            <ButtonImage
              name={`product-slider-${id}-image-button`}
              href={productUrl}
              noBorder
              className="u-position--relative"
              data-clientside-hook="productDetailButton"
              rel={isSponsored ? 'nofollow noopener' : undefined}
              isReactControlled
            >
              <ImageWithDiscountBadge
                imageUrl={thumbnailURL || imageUrl}
                srcWidth={srcWidth}
                srcHeight={srcHeight}
                alt={name}
                discountPercent={discountsToShow}
                thumbnail={thumbnail}
                lazyLoad={resolveLazyLoad(deviceClass, index, lazyLoad)}
                imageAttrs={{
                  'data-analytics': ANALYTICS_ATTR_IMAGE,
                }}
                sizes={imageSizes}
                minWidth={imageMinWidth}
                maxWidth={imageMaxWidth}
              />
            </ButtonImage>
          </div>
          <h3
            className="o-ProductSliderItem__header__title a-p u-margin-x-small--bottom o-ProductSliderItem__header__item"
            data-clientside-hook="stretchVertically"
            data-stretch-group="name"
          >
            <ButtonAsLink
              href={productUrl}
              name={`product-slider-${id}-link-button`}
              className="o-ProductSliderItem__link"
              data-analytics={ANALYTICS_ATTR_TITLE}
              title={name}
              data-clientside-hook="productDetailButton"
              rel={isSponsored ? 'nofollow noopener' : undefined}
              isReactControlled
            >
              {name}
            </ButtonAsLink>
          </h3>
          <StarRating
            rating={rating}
            ratingCount={numberOfRatings}
            centered
            invisible={!(rating > 0 || numberOfRatings > 0)}
            href={mountPoints.getProductReviews(productUrl)}
            isSponsored={isSponsored}
            crosssellClickTarget="rating"
          />
        </header>
        <ul className="o-ProductSliderItem__price-info a-list-reset">
          <li
            data-clientside-hook="stretchVertically"
            data-stretch-group="amount"
            className="o-ProductSliderItem__price-info__amount"
          >
            {amount}
            {Boolean(amount && pharmaForm) && ' | '}
            {pharmaForm}
          </li>
          <li
            data-clientside-hook="stretchVertically"
            data-stretch-group="product-price"
          >
            <ProductPrice
              prices={prices}
              size="x-small"
              displayInfo={false}
              showSuperScript={
                (shouldShowBuyButton() && tenant === TENANT_AT) ||
                tenant !== TENANT_AT
              }
              unitPricing={unitPricing}
              centered
              shouldDisplayAbPrice={shouldDisplayAbPrice}
            />
          </li>
        </ul>

        {shouldShowBuyButton() && (
          <div className="u-margin-small--top o-ProductSliderItem__add-to-cart__wrapper">
            <Button
              className="o-ProductSliderItem__add-to-cart"
              data-product-name={
                rxByPrincipleReasonSet ? product.name : undefined
              }
              data-offer-id={rxByPrincipleReasonSet ? offerId : undefined}
              data-rx-by-principle-reason-set={rxByPrincipleReasonSet}
              href={addToCartUrlWithTracking || addToCartPath}
              name={`product-slider-${id}-add-to-cart-button`}
              style="buttonGhost"
            >
              <Trans id="general.button.label.addToCart" />
            </Button>
          </div>
        )}
      </div>

      {goToProductButton && (
        <div className="u-margin-small--top">
          <Button
            href={productUrl}
            name={`product-highlights-cta-${product.id}`}
            style="buttonGhost"
            rel={isSponsored ? 'nofollow noopener' : undefined}
            isReactControlled
          >
            <Trans id="general.button.label.productLink" />
          </Button>
        </div>
      )}

      {Boolean(shippingPrice) && (shouldShowBuyButton() || goToProductButton) && (
        <div
          className={classNames({
            'u-padding-small--bottom': !showSellerBox,
          })}
        >
          <ProductSliderItemShippingPrice
            shippingPrice={shippingPrice}
            wid={wid}
          />
        </div>
      )}

      {showSellerBox && (
        <div className="a-box u-no-padding u-padding-small--bottom u-padding-small--sides u-text-align--center@small-tiny">
          <div className="a-p8 cross-sell-widget-text u-text-align--center@medium">
            <Trans id="message.soldAndShipped.partnerCrossSell" />
            <ButtonAsLink
              data-a11y-dialog-show={SELLER_MODAL_ID}
              href={`#${SELLER_MODAL_ID}`}
              data-clientside-hook={getSellerModalHook(sellerId)}
              className="partner-link-text u-padding-xx-small--left"
              data-qa-id="seller-name"
              data-target="seller-box"
              data-product-id={product.id}
            >
              {sellerName}
            </ButtonAsLink>
          </div>
        </div>
      )}
    </article>
  )
}

ProductSliderItem.propTypes = {
  publicRuntimeConfig: shape(publicRuntimeConfigType).isRequired,
  mountPoints: mountPointsType.isRequired,
  index: number,
  lazyLoad: bool,
  noBorder: bool,
  contextPid: string,
  product: shape(ProductType).isRequired,
  metadata: adServerMetadataType,
  showBuyButton: bool,
  goToProductButton: bool,
  noSlider: bool,
  redirectToCart: bool,
  srcHeight: number,
  srcWidth: number,
  imageMinWidth: number,
  imageMaxWidth: number,
  imageSizes: sizesPropType,
  thumbnail: bool,
  whiteImageBg: bool,
  wid: number,
}

ProductSliderItem.defaultProps = {
  index: -1,
  lazyLoad: null,
  noBorder: false,
  contextPid: null,
  metadata: {},
  showBuyButton: false,
  goToProductButton: false,
  noSlider: false,
  redirectToCart: false,
  srcHeight: 250,
  srcWidth: 250,
  imageMinWidth: null,
  imageMaxWidth: null,
  imageSizes: null,
  thumbnail: false,
  whiteImageBg: false,
  wid: -1,
}

export default compose(
  withMountPoints,
  withShopAndRuntimeConfig
)(ProductSliderItem)
